import { useMemo } from 'react'

import { Menu } from '@mantine/core'

import IconCheckmark from 'src/icons/IconCheckmark'
import IconChevronDown from 'src/icons/IconChevronDown'
import { cn } from 'src/utils'

import type { DashboardScope } from '../lib/dashboardScope'
import {
  SCOPE_ME,
  SCOPE_TEAM,
  scopeSoloEmployeeId,
} from '../lib/dashboardScope'

/**
 * Whose work the page is showing. Two segments — `Me`, and `Team`, where Team
 * carries the picker.
 *
 * Folding the picker into Team rather than giving it a third segment is what
 * makes the states honest: a chosen set of recruiters *is* a team, so it belongs
 * inside Team rather than beside it, and the picker's cleared state is exactly
 * the "no clause" the services already apply for an empty filter array. There is
 * no third state to keep in sync.
 *
 * Clicking Team enters the scope; clicking it again opens the picker. One
 * target, two jobs, in the order people want them.
 */
export interface ScopeOption {
  employeeId: string
  name: string
}

export interface ScopeControlProps {
  scope: DashboardScope
  onChange: (scope: DashboardScope) => void
  /**
   * Everyone who can own a booking or a task. Archived employees excluded.
   *
   * Includes admins and ops, not only recruiters: `Booking.employeeId` is a
   * plain foreign key to `Employee`, so their rows can appear on a company-wide
   * agenda and you need to be able to scope to them.
   */
  options: ScopeOption[]
  /** Used to collapse a one-person team back to that person's name. */
  myEmployeeId?: string | null
}

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')

/**
 * Up to three faces, then `+N`. A bare "3 people" names nothing, and Round 17
 * removed the widget subtitles that could have carried the scope instead — so
 * the control is the only place it can be read, and it has to be readable
 * without opening anything.
 */
const Faces = ({ names }: { names: string[] }) => (
  <span className="flex flex-row items-center">
    {names.slice(0, 3).map((name, index) => (
      <span
        key={name}
        className={cn(
          'flex h-[18px] w-[18px] items-center justify-center rounded-full bg-doubleNickel-brand-50 text-[8px] font-semibold text-doubleNickel-brand-700 ring-2 ring-white',
          index > 0 && '-ml-1.5'
        )}
      >
        {initialsOf(name)}
      </span>
    ))}
    {names.length > 3 && (
      <span className="ml-1 text-[10px] font-semibold text-doubleNickel-brand-600">
        +{names.length - 3}
      </span>
    )}
  </span>
)

const ScopeControl = ({
  scope,
  onChange,
  options,
  myEmployeeId,
}: ScopeControlProps) => {
  const isMe = scope.kind === 'ME'
  // Memoized rather than derived inline: a fresh `[]` on every render would give
  // the lookup below a new dependency each time and defeat its own memo.
  const selected = useMemo(
    () => (scope.kind === 'TEAM' ? scope.employeeIds : []),
    [scope]
  )
  const narrowed = selected.length > 0

  const nameById = useMemo(() => {
    const map = new Map<string, string>()
    options.forEach((option) => map.set(option.employeeId, option.name))
    return map
  }, [options])

  const selectedNames = useMemo(
    () => selected.map((id) => nameById.get(id) ?? 'Unknown employee'),
    [selected, nameById]
  )

  // A team of one is a person. Saying "1 person" would be technically true and
  // completely useless.
  const soloId = scopeSoloEmployeeId(scope, myEmployeeId)
  const soloName =
    scope.kind === 'TEAM' && soloId ? nameById.get(soloId) : undefined

  const toggle = (employeeId: string) => {
    const next = selected.includes(employeeId)
      ? selected.filter((id) => id !== employeeId)
      : [...selected, employeeId]
    onChange({ kind: 'TEAM', employeeIds: next })
  }

  // Ticking every name is *not* the same query as clearing: a list of people
  // cannot include work that belongs to nobody. Rather than silently snapping to
  // Everyone — which would make two different queries look like one state — the
  // picker says so, only at the moment it becomes true.
  const allTicked = options.length > 0 && selected.length === options.length

  return (
    <div className="flex flex-row items-center gap-1 rounded-lg border border-doubleNickel-gray-300 bg-white p-[3px]">
      <button
        type="button"
        aria-pressed={isMe}
        onClick={() => onChange(SCOPE_ME)}
        className={cn(
          'rounded-md px-3 py-1.5 text-xs font-medium',
          isMe
            ? 'bg-doubleNickel-brand-25 font-semibold text-doubleNickel-brand-700 ring-1 ring-inset ring-doubleNickel-brand-100'
            : 'text-doubleNickel-gray-600 hover:bg-doubleNickel-gray-50 hover:text-doubleNickel-gray-900'
        )}
      >
        Me
      </button>

      <Menu
        position="bottom-end"
        width={272}
        closeOnItemClick={false}
        shadow="md"
      >
        <Menu.Target>
          <button
            type="button"
            aria-pressed={!isMe}
            aria-label="Show the team's work"
            onClick={() => {
              // First click enters the scope. The menu opens on top of that,
              // which is what people expect from a control that is both.
              if (isMe) onChange(SCOPE_TEAM)
            }}
            className={cn(
              'flex flex-row items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium',
              !isMe
                ? 'bg-doubleNickel-brand-25 font-semibold text-doubleNickel-brand-700 ring-1 ring-inset ring-doubleNickel-brand-100'
                : 'text-doubleNickel-gray-600 hover:bg-doubleNickel-gray-50 hover:text-doubleNickel-gray-900'
            )}
          >
            {soloName ? (
              <span className="max-w-[140px] truncate">{soloName}</span>
            ) : (
              <>
                <span>Team</span>
                {narrowed && <Faces names={selectedNames} />}
              </>
            )}
            <IconChevronDown className="h-3 w-3 flex-shrink-0 fill-none stroke-current" />
          </button>
        </Menu.Target>

        <Menu.Dropdown>
          {/* Everyone leads the list rather than sitting behind a "clear"
              affordance: it is a scope in its own right, and the only one that
              includes work nobody owns. */}
          <Menu.Item
            onClick={() => onChange(SCOPE_TEAM)}
            leftSection={
              <span
                className={cn(
                  'flex h-4 w-4 items-center justify-center rounded border',
                  narrowed
                    ? 'border-doubleNickel-gray-300'
                    : 'border-doubleNickel-brand-600 bg-doubleNickel-brand-600'
                )}
              >
                {!narrowed && (
                  <IconCheckmark className="h-2.5 w-2.5 fill-none stroke-white stroke-[3]" />
                )}
              </span>
            }
          >
            <div className="flex flex-row items-center justify-between gap-2">
              <span className="text-xs font-semibold text-doubleNickel-gray-900">
                Everyone
              </span>
              <span className="flex-shrink-0 text-[10px] text-doubleNickel-gray-500">
                {options.length} people + unowned
              </span>
            </div>
          </Menu.Item>

          <Menu.Divider />

          <div className="max-h-[260px] overflow-y-auto">
            {options.map((option) => {
              const on = selected.includes(option.employeeId)
              return (
                <Menu.Item
                  key={option.employeeId}
                  onClick={() => toggle(option.employeeId)}
                  leftSection={
                    <span
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded border',
                        on
                          ? 'border-doubleNickel-brand-600 bg-doubleNickel-brand-600'
                          : 'border-doubleNickel-gray-300'
                      )}
                    >
                      {on && (
                        <IconCheckmark className="h-2.5 w-2.5 fill-none stroke-white stroke-[3]" />
                      )}
                    </span>
                  }
                >
                  <span className="block min-w-0 truncate text-xs text-doubleNickel-gray-800">
                    {option.name}
                  </span>
                </Menu.Item>
              )
            })}
            {options.length === 0 && (
              <div className="px-3 py-2 text-xs text-doubleNickel-gray-500">
                No other employees
              </div>
            )}
          </div>

          {allTicked && (
            <div className="border-t border-doubleNickel-warning-200 bg-doubleNickel-warning-50 px-3 py-2 text-[10px] leading-snug text-doubleNickel-warning-700">
              Everyone is ticked, but that is not the same as{' '}
              <span className="font-semibold">Everyone</span> — a list of people
              cannot include work nobody owns.
            </div>
          )}
        </Menu.Dropdown>
      </Menu>
    </div>
  )
}

export default ScopeControl
